/* eslint-disable react/jsx-props-no-spreading */
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { notifications } from '@mantine/notifications';

import {
  Button,
  Card,
  Center,
  Divider,
  Flex,
  Loader,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  NumberInput,
  Title,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { isNotEmpty, useForm } from '@mantine/form';

import {
  MENU_OBJECT_TYPES,
  MENU_NAME_MAX_LENGTH,
  MENU_TYPES,
  SAVE_ERROR_MESSAGE,
  VIEW_MODE,
} from 'constants/menuManagement';
import { TIME_24_HOUR_FORMAT } from 'constants/dateTime';
import { LOCATION_PATH, MENU_PATH } from 'constants/path';
import { breakpoints } from 'styles/theme.variables';
import { NOTIFICATION_VARIANTS } from 'constants/chownow';

import { useCubohActionBar } from 'hooks/useCubohActionBar';
import { useHasCateringScope } from 'hooks/useHasCateringScope';
import { useMenuManagement } from 'hooks/useMenuManagement';
import { useAuth } from 'hooks/useAuth';

import {
  cleanMenuSchedules,
  getNewMenuSchedules,
  sortByDateAndTime,
} from 'helpers/menuManagementHours';
import { generateTempId } from 'helpers/utils';
import { formatNotificationTimestamp } from 'helpers/format';

import {
  splitAllSchedulesRolloverHours,
  hasRolloverHours,
} from 'components/MenuManagement/MenuEdit/Hours/ScheduleSelect/helpers';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import { MenuFormButtons } from 'components/MenuManagement/MenuEdit/MenuFormButtons';
import Hours from 'components/MenuManagement/MenuEdit/Hours/Hours';
import Integrations from 'components/MenuManagement/MenuEdit/Integrations/Integrations';
import LinkedMerchants from 'components/MenuManagement/MenuEdit/LinkedMerchants/LinkedMerchants';
import PublishResultItem from 'components/MenuManagement/MenuEdit/PublishResultItem/PublishResultItem';
import PublishResultsModal from 'components/MenuManagement/MenuEdit/PublishResultsModal/PublishResultsModal';
import { validateMenuSchedules } from 'components/MenuManagement/MenuEdit/validation';
import {
  checkIfStatesTouched,
  getIntegrations,
  getProcessedSchedules,
} from 'components/MenuManagement/MenuEdit/helpers';

const SPECIAL_INSTRUCTIONS_INTEGRATIONS = ['ritual', 'ubereats'];

// remove uuid from itemHours to use for deliveryHours
const getCleanedItemHoursForDelivery = itemHours => {
  if (!itemHours) return [];
  return itemHours.map(({ uuid, ...schedule }) => ({
    ...schedule,
    tempId: generateTempId(),
  }));
};

// mark periods and sort by time
const processSchedulesWithPeriods = hours => {
  if (!hours?.length) return [];

  // group schedules by day to separate primary hours and periods
  const groupedSchedules = hours.reduce(
    (schedulesByDay, schedule) => ({
      ...schedulesByDay,
      [schedule.dayIndex]: [...(schedulesByDay[schedule.dayIndex] || []), schedule],
    }),
    {},
  );

  return Object.values(groupedSchedules).flatMap(daySchedules => {
    // sort schedules within each day by start time
    const sortedDaySchedules = [...daySchedules].sort((a, b) => {
      const start1 = dayjs(a.startTime, TIME_24_HOUR_FORMAT);
      const start2 = dayjs(b.startTime, TIME_24_HOUR_FORMAT);
      return start1.diff(start2);
    });

    // mark the earliest schedule as primary, rest as periods
    return sortedDaySchedules.map((schedule, index) => ({
      ...schedule,
      isPeriod: index > 0,
      tempId: schedule.uuid || schedule.tempId || generateTempId(),
    }));
  });
};

export default function MenuInfo() {
  const { createMenu, menu, cateringData, setHasHoursErrors } = useContext(MenuEdit.Context);
  const isCateringEnabled = cateringData?.is_enabled;
  const hasCateringScope = useHasCateringScope();
  const isEditingExisting = !createMenu;
  const navigate = useNavigate();
  const { restaurantId } = useParams();
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);
  const persistNotification = { withCloseButton: true, autoClose: false };
  const cautionNotification = {
    ...persistNotification,
    variant: NOTIFICATION_VARIANTS.caution.label,
    icon: NOTIFICATION_VARIANTS.caution.icon,
  };

  const [opened, { open, close }] = useDisclosure();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [schedules, setSchedules] = useState(getNewMenuSchedules());
  const [specialHours, setSpecialHours] = useState([]);
  const [deliveryHours, setDeliveryHours] = useState(getNewMenuSchedules());
  const [selectedIntegrations, setSelectedIntegrations] = useState([]);
  const [selectedLinkedMerchants, setSelectedLinkedMerchants] = useState(
    menu?.associatedMerchants || [],
  );
  const [menuEnabled, setMenuEnabled] = useState();
  const [validationErrors, setValidationErrors] = useState([]);
  const [menuNameLength, setMenuNameLength] = useState(menu?.name?.length || 0);

  const { updateCubohMenu, setCreateMenu, createNewCubohItem, getSingleCubohMenu } =
    useMenuManagement();
  const { singleSelectedCompanyID } = useCubohActionBar();
  const { cubohUser, companies, isInternalUser } = useAuth();

  // Determine if user is a single-company company-level user
  // CN admin auth only returns one company/merchant for the restaurant they navigated to,
  // which would incorrectly classify them as a single-company user
  const activeCompanies = Object.values(companies || {}).filter(company => company.active);
  const isSingleCompanyUser =
    !isInternalUser && !cubohUser?.isParentCompanyUser && activeCompanies.length === 1;

  const [initialViewMode, setInitialViewMode] = useState();

  // Store initial state values for comparison
  const [initialStates, setInitialStates] = useState({
    schedules: getNewMenuSchedules(),
    specialHours: [],
    deliveryHours: getNewMenuSchedules(),
    selectedIntegrations: [],
    selectedLinkedMerchants: [],
    menuEnabled: undefined,
  });

  useEffect(() => {
    if (!menu) return;

    if (menu.itemHours) {
      // Group schedules by day to separate primary hours and periods
      const groupedSchedules = menu.itemHours.reduce(
        (schedulesByDay, schedule) => ({
          ...schedulesByDay,
          [schedule.dayIndex]: [...(schedulesByDay[schedule.dayIndex] || []), schedule],
        }),
        {},
      );

      const schedulesWithPeriods = Object.values(groupedSchedules).flatMap(daySchedules => {
        // sort schedules within each day by start time
        const sortedDaySchedules = [...daySchedules].sort((a, b) => {
          const start1 = dayjs(a.startTime, TIME_24_HOUR_FORMAT);
          const start2 = dayjs(b.startTime, TIME_24_HOUR_FORMAT);
          return start1.diff(start2);
        });

        // mark the earliest schedule as primary, rest as periods
        // otherwise user can delete the primary schedule and mess up sorting
        return sortedDaySchedules.map((schedule, index) => ({
          ...schedule,
          isPeriod: index > 0,
        }));
      });

      const hasRolloverSchedules = schedulesWithPeriods.some(hasRolloverHours);
      setInitialViewMode(hasRolloverSchedules ? VIEW_MODE.INPUT : VIEW_MODE.TIMELINE);
      setSchedules(schedulesWithPeriods);
    }

    if (menu.specialHours) {
      const sortedSpecialHours = [...menu.specialHours].sort(sortByDateAndTime);
      setSpecialHours(sortedSpecialHours);
    }

    const hours =
      menu.deliveryMatchesPickup === false && menu.deliveryHours
        ? menu.deliveryHours
        : getCleanedItemHoursForDelivery(menu.itemHours);

    setDeliveryHours(processSchedulesWithPeriods(hours));

    setMenuEnabled(menu.enabled);

    // Update initial states when menu is loaded (for existing menus)
    if (isEditingExisting) {
      const processedSchedules = menu.itemHours
        ? getProcessedSchedules(menu.itemHours)
        : getNewMenuSchedules();

      const integrations = getIntegrations(menu);

      setInitialStates({
        schedules: processedSchedules,
        specialHours: menu.specialHours ? [...menu.specialHours].sort(sortByDateAndTime) : [],
        deliveryHours: processSchedulesWithPeriods(hours),
        selectedLinkedMerchants: menu.associatedMerchants || [],
        selectedIntegrations: integrations,
        menuEnabled: menu.enabled,
      });
    }
  }, [menu, isEditingExisting]);

  useEffect(() => {
    if (menu?.name) setMenuNameLength(menu.name.length);
  }, [menu?.name]);

  const initialValues = isEditingExisting
    ? {
        menuType: menu?.menuType || MENU_TYPES.STANDARD,
        name: menu?.name,
        description: menu?.description,
        taxRate: menu?.taxRate,
        menuId: menu?.uuid,
        specialInstructions: menu?.specialInstructionsEnabled,
      }
    : {
        menuType: MENU_TYPES.STANDARD,
      };

  const form = useForm({
    mode: 'uncontrolled',
    initialValues,
    validate: {
      menuType: isNotEmpty('Menu type is required'),
      name: value => {
        if (!value || !value.trim()) return 'Menu name is required';
        if (value.length > MENU_NAME_MAX_LENGTH)
          return `Menu name cannot exceed ${MENU_NAME_MAX_LENGTH} characters`;
        return null;
      },
      taxRate: isNotEmpty('Tax Rate is required'),
    },
  });

  form.watch('name', ({ value }) => {
    setMenuNameLength(value?.length || 0);
  });

  const showSpecialInstructionsSwitch = menu?.integrations?.some(integration =>
    SPECIAL_INSTRUCTIONS_INTEGRATIONS.includes(integration.integrationName),
  );

  function getUpdatedIntegrationData() {
    const updatedIntegrations = [];

    selectedIntegrations.forEach(integration => {
      const savedIntegration = menu?.integrations.find(
        menuIntegration => menuIntegration.integrationName === integration,
      );
      if (savedIntegration) {
        updatedIntegrations.push(savedIntegration);
      } else {
        updatedIntegrations.push({ integrationName: integration });
      }
    });

    menu?.integrations.forEach(menuIntegration => {
      if (!selectedIntegrations.includes(menuIntegration.integrationName)) {
        updatedIntegrations.push({ ...menuIntegration, isDeleted: true });
      }
    });

    return updatedIntegrations;
  }

  const validateAndHandleErrors = errors => {
    if (errors.length > 0) {
      setValidationErrors(errors);
      setHasHoursErrors?.(true);
      setIsSubmitting(false);
      return true;
    }
    return false;
  };

  const validateSchedules = (pickupSchedules, deliverySchedules) =>
    validateMenuSchedules(pickupSchedules, deliverySchedules);

  const handleSave = async values => {
    setIsSubmitting(true);
    try {
      const formattedValues = { ...values, taxRate: parseFloat(values.taxRate) };
      if (isEditingExisting) {
        const integrationData = getUpdatedIntegrationData();
        const sortedSpecialHours = [...specialHours].sort(sortByDateAndTime);

        const transformedSchedules = splitAllSchedulesRolloverHours(schedules, {
          forBackend: true,
        });
        const transformedSpecialHours = splitAllSchedulesRolloverHours(sortedSpecialHours, {
          forBackend: true,
          isSpecial: true,
        });
        const transformedDeliveryHours = splitAllSchedulesRolloverHours(deliveryHours, {
          forBackend: true,
        });

        const allErrors = validateSchedules(transformedSchedules, transformedDeliveryHours);
        if (validateAndHandleErrors(allErrors)) return;

        // remove deliveryMatchesPickup from menu before spreading since it's calculated by the BE
        const { deliveryMatchesPickup, ...menuWithoutDeliveryMatches } = menu;
        const response = await updateCubohMenu({
          group: MENU_OBJECT_TYPES.single,
          id: menu.key,
          data: {
            ...menuWithoutDeliveryMatches,
            ...formattedValues,
            itemHours: cleanMenuSchedules(transformedSchedules),
            specialHours: cleanMenuSchedules(transformedSpecialHours, { isSpecial: true }),
            deliveryHours: cleanMenuSchedules(transformedDeliveryHours),
            integrations: integrationData,
            associatedMerchants: selectedLinkedMerchants,
            specialInstructionsEnabled: formattedValues.specialInstructions,
            enabled: menuEnabled,
          },
        });

        if (response?.errorMessage) {
          notifications.show({
            message: (
              <span data-testid="menu-save-error-notification">
                {response.errorMessage || SAVE_ERROR_MESSAGE}
              </span>
            ),
            closeButtonProps: { 'data-testid': 'notification-close-button' },
            ...cautionNotification,
          });
        } else if (!response) {
          notifications.show({
            message: (
              <span data-testid="menu-save-error-notification">{SAVE_ERROR_MESSAGE}</span>
            ),
            closeButtonProps: { 'data-testid': 'notification-close-button' },
            ...cautionNotification,
          });
        } else {
          await getSingleCubohMenu(menu.companyId, menu.merchantId, menu.uuid);
          notifications.show({
            message: (
              <span data-testid="menu-save-success-notification">
                {`You have successfully saved '${formattedValues.name}' on ${formatNotificationTimestamp()}.`}
              </span>
            ),
            closeButtonProps: { 'data-testid': 'notification-close-button' },
            variant: NOTIFICATION_VARIANTS.success.label,
            icon: NOTIFICATION_VARIANTS.success.icon,
            ...persistNotification,
          });
          form.resetDirty();
        }
      } else {
        const transformedSchedules = splitAllSchedulesRolloverHours(schedules, {
          forBackend: true,
        });
        const transformedDeliveryHours = splitAllSchedulesRolloverHours(deliveryHours, {
          forBackend: true,
        });

        const allErrors = validateSchedules(transformedSchedules, transformedDeliveryHours);
        if (validateAndHandleErrors(allErrors)) return;

        const updatedMenu = {
          ...formattedValues,
          itemHours: cleanMenuSchedules(transformedSchedules),
          merchantId: createMenu,
          companyId: singleSelectedCompanyID,
          enabled: true,
          parentUuid: null,
          itemType: MENU_OBJECT_TYPES.menus,
          deliveryHours: cleanMenuSchedules(transformedDeliveryHours),
        };
        const response = await createNewCubohItem({
          group: MENU_OBJECT_TYPES.menus,
          data: updatedMenu,
        });

        if (response?.errorMessage) {
          notifications.show({
            message: (
              <span data-testid="menu-create-error-notification">
                {response.errorMessage || SAVE_ERROR_MESSAGE}
              </span>
            ),
            closeButtonProps: { 'data-testid': 'notification-close-button' },
            ...cautionNotification,
          });
        } else if (response?.uuid) {
          setCreateMenu(null);
          window.location.href = `${LOCATION_PATH}/${restaurantId}${MENU_PATH}/edit?merchantId=${response.merchantId}&menuId=${response.uuid}`;
        } else {
          notifications.show({
            message: (
              <span data-testid="menu-create-error-notification">{SAVE_ERROR_MESSAGE}</span>
            ),
            closeButtonProps: { 'data-testid': 'notification-close-button' },
            ...cautionNotification,
          });
        }
      }
    } catch {
      notifications.show({
        message: SAVE_ERROR_MESSAGE,
        ...cautionNotification,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    form.reset();

    if (!isEditingExisting || !menu?.itemHours) {
      const newSchedules = getNewMenuSchedules();

      setSchedules(newSchedules);
      setSpecialHours([]);
      setDeliveryHours(newSchedules);
      setSelectedLinkedMerchants([]);
      setSelectedIntegrations([]);
      setMenuEnabled(undefined);

      // Reset initial states for new menus
      setInitialStates({
        schedules: newSchedules,
        specialHours: [],
        deliveryHours: newSchedules,
        selectedIntegrations: [],
        selectedLinkedMerchants: [],
        menuEnabled: undefined,
      });
      return;
    }

    // Reset to original menu values for existing menus
    const processedSchedules = processSchedulesWithPeriods(menu.itemHours);
    const sortedSpecialHours = [...(menu.specialHours || [])].sort(sortByDateAndTime);
    const hours =
      menu.deliveryMatchesPickup === false && menu.deliveryHours
        ? menu.deliveryHours
        : getCleanedItemHoursForDelivery(menu.itemHours);
    const processedDeliveryHours = processSchedulesWithPeriods(hours);
    const merchants = menu.associatedMerchants || [];
    const integrations = menu.integrations?.map(integration => integration.integrationName) || [];

    setSchedules(processedSchedules);
    setSpecialHours(sortedSpecialHours);
    setDeliveryHours(processedDeliveryHours);
    setSelectedLinkedMerchants(merchants);
    setSelectedIntegrations(integrations);
    setMenuEnabled(menu.enabled);

    // Reset initial states to original menu values
    setInitialStates({
      schedules: processedSchedules,
      specialHours: sortedSpecialHours,
      deliveryHours: processedDeliveryHours,
      selectedIntegrations: integrations,
      selectedLinkedMerchants: merchants,
      menuEnabled: menu.enabled,
    });
  };

  const checkMenuTouched = () => {
    if (form.isDirty()) {
      return true;
    }

    return checkIfStatesTouched({
      initialStates,
      schedules,
      specialHours,
      deliveryHours,
      selectedIntegrations,
      selectedLinkedMerchants,
      menuEnabled,
    });
  };

  // clear validation errors when user makes edits
  useEffect(() => {
    setValidationErrors([]);
    setHasHoursErrors?.(false);
  }, [schedules, deliveryHours]);

  return (
    <form noValidate onSubmit={form.onSubmit(values => handleSave(values))} onReset={handleReset}>
      <Stack gap="xl">
        <Card p={{ base: 'md', md: 'xl' }}>
          <Stack gap="lg">
            <Title order={6}>Menu Info</Title>
            <Stack gap="lg">
              {hasCateringScope && isCateringEnabled && (
                <Select
                  label="Menu type"
                  required
                  size="md"
                  allowDeselect={false}
                  error={form.errors.menuType}
                  maw={600}
                  data={[
                    { value: MENU_TYPES.STANDARD, label: 'Standard' },
                    { value: MENU_TYPES.CATERING, label: 'Catering' },
                  ]}
                  {...form.getInputProps('menuType')}
                />
              )}
              <TextInput
                label="Menu name"
                required
                size="md"
                key={form.key('name')}
                maw={600}
                maxLength={MENU_NAME_MAX_LENGTH}
                description={
                  <Flex justify="space-between">
                    <Text variant="sublabel-xs" c="var(--mantine-color-error)">
                      {form.errors.name || ''}
                    </Text>
                    <Text variant="caption-xs2">{`${menuNameLength}/${MENU_NAME_MAX_LENGTH}`}</Text>
                  </Flex>
                }
                {...form.getInputProps('name')}
                error={!!form.errors.name}
              />
              <TextInput
                label="Description"
                key={form.key('description')}
                maw={600}
                {...form.getInputProps('description')}
              />
              <NumberInput
                label="Default tax rate"
                allowNegative={false}
                suffix="%"
                rightSection={null}
                hideControls
                required
                mb={0}
                error={form.errors.taxRate}
                key={form.key('taxRate')}
                maw={600}
                {...form.getInputProps('taxRate')}
              />
              {isEditingExisting && (
                <>
                  <TextInput
                    disabled
                    label="Menu ID"
                    key={form.key('menuId')}
                    maw={600}
                    {...form.getInputProps('menuId')}
                  />
                  {showSpecialInstructionsSwitch && (
                    <Switch
                      label="Special instructions (only supported by Ritual and UberEats)"
                      key={form.key('specialInstructions')}
                      {...form.getInputProps('specialInstructions')}
                      defaultChecked={form.values.specialInstructions}
                    />
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Card>

        <Card p={{ base: 'md', md: 'xl' }} mb={!isEditingExisting ? '140px' : undefined}>
          <Stack gap="lg">
            {/* render hours after we know the correct view mode to prevent flash */}
            {isEditingExisting && menu?.itemHours && initialViewMode === undefined ? (
              <Center py="xl">
                <Loader />
              </Center>
            ) : (
              <Hours
                isEditingExisting={isEditingExisting}
                schedules={schedules}
                setSchedules={setSchedules}
                setSpecialHours={setSpecialHours}
                specialHours={specialHours}
                deliveryHours={deliveryHours}
                setDeliveryHours={setDeliveryHours}
                validationErrors={validationErrors}
                deliveryMatchesPickup={menu?.deliveryMatchesPickup}
                initialViewMode={initialViewMode}
              />
            )}
          </Stack>
        </Card>

        {isEditingExisting && (
          <>
            <Card p={{ base: 'md', md: 'xl' }}>
              <Stack gap="lg">
                <Title order={6}>Integrations</Title>
                <Integrations
                  menu={menu}
                  selectedIntegrations={selectedIntegrations}
                  setSelectedIntegrations={setSelectedIntegrations}
                />
              </Stack>
            </Card>
            {!isSingleCompanyUser && !isInternalUser && (
              <Card p={{ base: 'md', md: 'xl' }}>
                <Stack gap="lg">
                  <Title order={6}>Linked Merchants</Title>
                  <LinkedMerchants
                    menu={menu}
                    selectedLinkedMerchants={selectedLinkedMerchants}
                    setSelectedLinkedMerchants={setSelectedLinkedMerchants}
                  />
                </Stack>
              </Card>
            )}

            <Card p={{ base: 'md', md: 'xl' }} mb={140}>
              <Stack gap="lg">
                <Title order={6}>Recently Published</Title>
                <Card withBorder radius="xs" p="sm" maw={600} variant="outline-secondary">
                  {!menu?.publishResults.length ? (
                    <Text variant="sublabel-sm" c="var(--content-secondary)">
                      No results
                    </Text>
                  ) : (
                    <Stack>
                      {menu.publishResults.slice(0, 2).map((result, index) => (
                        <Stack key={result.seqId}>
                          <PublishResultItem result={result} />
                          {menu.publishResults.length > index + 1 && <Divider variant="dotted" />}
                        </Stack>
                      ))}
                      {menu.publishResults.length > 2 && (
                        <Button variant="link" h="auto" tt="none" onClick={open}>
                          More results
                        </Button>
                      )}
                      <PublishResultsModal
                        opened={opened}
                        close={close}
                        publishResults={menu.publishResults}
                      />
                    </Stack>
                  )}
                </Card>
              </Stack>
            </Card>
          </>
        )}
      </Stack>

      <MenuFormButtons
        isEdit={isEditingExisting}
        menu={menu ? { ...menu, enabled: menuEnabled ?? menu.enabled } : {}}
        formSubmitted={isSubmitting}
        resetFields={handleReset}
        checkMenuTouched={checkMenuTouched}
        setMenuEnabled={setMenuEnabled}
        handleSave={form.onSubmit(values => handleSave(values))}
      />
    </form>
  );
}
